/**
 * Catalog of agent passives — mirrors src/data/passives.ts but for the
 * agent side. Rolled procedurally by generateAgentLoadout() based on the
 * current run round. Phase 1 ships this as data-only; the dossier panel
 * renders the name/description but the runtime effects are wired up in
 * Phase 2 at each system's seam (agentAI.ts for prep/AP, detection.ts
 * for range caps, the onmessage handler for junk-card injection).
 *
 * Design notes per key:
 *
 * - hardened  → +2 prep per agent turn, capped at 75% accumulated prep.
 *               Hook: runAgentTurn top-of-turn prepBonus increment.
 *
 * - extraClip → +1 AP per agent turn. Hook: runAgentTurn top-of-turn
 *               agent.ap += 1 before the card-play loop.
 *
 * - ping     →  Every 3 turns, inject a one-shot 'Ping' junk card into
 *               the PLAYER's hand (src/data/cards.ts playerPingJunk).
 *               The card costs 1 AP to discard; if still in the player's
 *               hand at end of turn, the agent gets the player's exact
 *               position revealed for 2 turns.
 *               Hook: post-agent-turn onmessage reducer.
 *
 * - eagleEye → Agent vision cone range +50%. Hook: detection.ts
 *              agentCanSeePlayer (and the mid-path agent detection) —
 *              multiply tuning.visionConeRange by 1.5 when the agent has
 *              this passive.
 *
 * - deepCover → Caps the PLAYER's effective detection range against
 *               THIS agent at 100m, regardless of binoculars or rooftop
 *               multipliers. The visual cone still renders at its full
 *               range (so the player can see where their line of sight
 *               SHOULD reach), but a secondary 100m arc is overlaid to
 *               show the actual detection zone. Hook: detection.ts
 *               playerCanSeeAgent + checkPlayerMidPathDetection +
 *               checkAgentMidPathDetection + MapRenderer cone renderer.
 */

import type { AgentPassive } from '../types/game';

export const AGENT_PASSIVES: Record<AgentPassive['key'], AgentPassive> = {
  hardened: {
    key: 'hardened',
    name: 'Hardened',
    description:
      'Agent gains +2 prep per turn, capped at 75%. Builds combat odds steadily over the round, making mutual encounters much riskier the longer you stall.',
  },
  extraClip: {
    key: 'extraClip',
    name: 'Extra Clip',
    description:
      'Agent gets +1 AP every turn. Expect more aggressive card plays — additional moves, sweeps, or intel pulls per round.',
  },
  ping: {
    key: 'ping',
    name: 'Ping',
    description:
      "Every 3 turns a 'Ping' junk card lands in your hand. Costs 1 AP to discard. If you end the turn with it still in hand, your exact position is broadcast to the agent for 2 turns.",
  },
  eagleEye: {
    key: 'eagleEye',
    name: 'Eagle Eye',
    description:
      'Agent vision cone range +50%. Longer sight lines, longer overwatch distance — scouting runs near this agent are substantially more dangerous.',
  },
  deepCover: {
    key: 'deepCover',
    name: 'Deep Cover',
    description:
      'Your binoculars and rooftop vision have no effect against this agent — your detection range is hard-capped at 100m regardless of gear. You can still see where your cone reaches, but the agent only registers up close.',
  },
};

/** Convenience accessor used by the loadout generator. */
export function getAgentPassive(key: AgentPassive['key']): AgentPassive {
  return AGENT_PASSIVES[key];
}

/** Check whether an agent has a specific passive. Used at every Phase 2 hook site. */
export function agentHasPassive(agent: { loadout: { passives: AgentPassive[] } }, key: AgentPassive['key']): boolean {
  return agent.loadout.passives.some(p => p.key === key);
}

/** All passive keys — used for random rolls. */
export const ALL_AGENT_PASSIVE_KEYS: AgentPassive['key'][] = [
  'hardened',
  'extraClip',
  'ping',
  'eagleEye',
  'deepCover',
];

/** "Softer" passives used for the round-2 pool (lower impact). */
export const SOFT_AGENT_PASSIVE_KEYS: AgentPassive['key'][] = [
  'hardened',
  'extraClip',
];
